import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";
import type { Schemas } from "@/types/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api-typed";
import type { BodyOf } from "@/types/api";
import { PENDING_INVITES_KEY, calendarErrorMessage } from "./use-calendar-availability";

// Surface server errors via toast so a failed mutation never dies silently.
// Callers can still attach their own onError for context-specific handling.
const toastMutationError = (err: unknown, fallback: string) => {
  toast.error(calendarErrorMessage(err, fallback));
};

// ─── Types ──────────────────────────────────────────────────────────────────

export type CalendarEvent = Schemas["CalendarEventRead"];

export type AttendeeCounts = Schemas["AttendeeCounts"];

export type CalendarEventAttendee = Schemas["CalendarEventAttendeeRead"];

export type CalendarEventCreate = BodyOf<"/hr/calendar/events", "post">;

export type CalendarEventUpdate = BodyOf<"/hr/calendar/events/{event_id}", "patch">;

export interface CalendarEventListResponse {
  items: CalendarEvent[];
  total: number;
}

export type MeetingRoom = Schemas["MeetingRoomRead"];

export type MeetingRoomCreate = Schemas["MeetingRoomCreate"];

export type MeetingRoomUpdate = Schemas["MeetingRoomUpdate"];


export interface CalendarEventListParams {
  type?: string;
  start_date?: string;
  end_date?: string;
  department_id?: string;
  room_id?: string;
  only_attendee?: boolean;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/** `enabled=false` não consulta: o calendário do núcleo só pede o que o perfil tem (a rota é de RH/Segurança/Receção). */
export const useCalendarEvents = (params: CalendarEventListParams = {}, enabled = true) => {
  return useQuery({
    queryKey: ["calendar-events", params],
    enabled,
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params.type) queryParams.append("type", params.type);
      if (params.start_date) queryParams.append("start_date", params.start_date);
      if (params.end_date) queryParams.append("end_date", params.end_date);
      if (params.department_id) queryParams.append("department_id", params.department_id);
      if (params.room_id) queryParams.append("room_id", params.room_id);
      if (params.only_attendee) queryParams.append("only_attendee", "true");
      void queryParams;
      return api.get("/hr/calendar/events", {
        query: {
          type: params.type || undefined,
          start_date: params.start_date || undefined,
          end_date: params.end_date || undefined,
          department_id: params.department_id || undefined,
          room_id: params.room_id || undefined,
          only_attendee: params.only_attendee || undefined,
        },
      });
    },
  });
};

// Single helper invalidates every query that may show calendar events.
// Used after create / update / delete / RSVP so all calendars refresh together.
const CALENDAR_INVALIDATION_KEYS = [["calendar-events"], ["me", "today"]];
const invalidateAllCalendarQueries = (qc: ReturnType<typeof useQueryClient>) => {
  CALENDAR_INVALIDATION_KEYS.forEach((queryKey) => qc.invalidateQueries({ queryKey }));
};

export const useCreateCalendarEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CalendarEventCreate) => {
      return api.post("/hr/calendar/events", { body: data });
    },
    onSuccess: (created) => {
      // Optimistic insert into every cached event list before the server round-trip
      // so the new event appears instantly on whichever calendar is mounted.
      queryClient.setQueriesData<CalendarEventListResponse>(
        { queryKey: ["calendar-events"] },
        (old) => {
          if (!old) return old;
          if (old.items.some((e) => e.id === created.id)) return old;
          return { items: [...old.items, created], total: old.total + 1 };
        },
      );
      invalidateAllCalendarQueries(queryClient);
    },
    onError: (err) => toastMutationError(err, "Erro ao criar evento"),
  });
};

export const useUpdateCalendarEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // `notifyAttendees`: «Notificar os convidados?» — sem ele o servidor avisa (o seu default); `false` grava sem avisar ninguém.
    mutationFn: async ({ id, notifyAttendees, ...data }: CalendarEventUpdate & { id: string; notifyAttendees?: boolean }) => {
      return api.patch("/hr/calendar/events/{event_id}", { path: { event_id: id }, query: { notify_attendees: notifyAttendees }, body: data });
    },
    onSuccess: (updated) => {
      queryClient.setQueriesData<CalendarEventListResponse>(
        { queryKey: ["calendar-events"] },
        (old) => {
          if (!old) return old;
          return {
            items: old.items.map((e) => (e.id === updated.id ? updated : e)),
            total: old.total,
          };
        },
      );
      invalidateAllCalendarQueries(queryClient);
    },
    onError: (err) => toastMutationError(err, "Erro ao atualizar evento"),
  });
};

export const useDeleteCalendarEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return api.delete("/hr/calendar/events/{event_id}", { path: { event_id: id } });
    },
    onSuccess: (_data, deletedId) => {
      queryClient.setQueriesData<CalendarEventListResponse>(
        { queryKey: ["calendar-events"] },
        (old) => {
          if (!old) return old;
          return {
            items: old.items.filter((e) => e.id !== deletedId),
            total: Math.max(0, old.total - 1),
          };
        },
      );
      invalidateAllCalendarQueries(queryClient);
    },
    onError: (err) => toastMutationError(err, "Erro ao eliminar evento"),
  });
};

/** «Só este» (`this`) ou «este e os seguintes» (`following`) numa série recorrente. */
export type SeriesScope = "this" | "following";

/** Edita só esta ocorrência, ou esta e as seguintes. Devolve o evento que nasce da edição (avulso ou a série nova). */
export const useUpdateCalendarOccurrence = () => {
  return useMutationWithToast({
    mutationFn: async ({ id, scope, occurrenceDate, notifyAttendees, ...data }: CalendarEventUpdate & { id: string; scope: SeriesScope; occurrenceDate: string; notifyAttendees?: boolean }) =>
      api.patch("/hr/calendar/events/{event_id}/occurrence", {
        path: { event_id: id },
        query: { scope, occurrence_date: occurrenceDate, notify_attendees: notifyAttendees },
        body: data,
      }),
    invalidateQueries: CALENDAR_INVALIDATION_KEYS,
    onErrorCallback: (err) => toastMutationError(err, "Erro ao atualizar o evento"),
  });
};

/** Apaga só esta ocorrência, ou esta e as seguintes. */
export const useDeleteCalendarOccurrence = () => {
  return useMutationWithToast({
    mutationFn: async ({ id, scope, occurrenceDate }: { id: string; scope: SeriesScope; occurrenceDate: string }) =>
      api.delete("/hr/calendar/events/{event_id}/occurrence", { path: { event_id: id }, query: { scope: scope, occurrence_date: occurrenceDate } }),
    invalidateQueries: CALENDAR_INVALIDATION_KEYS,
    onErrorCallback: (err) => toastMutationError(err, "Erro ao eliminar o evento"),
  });
};

// ─── Meeting Rooms ──────────────────────────────────────────────────────────

/** `enabled=false` não consulta: as salas vivem na API do calendário, fechada em perfis sem RH/Segurança/Receção. */
export const useMeetingRooms = (enabled = true) => {
  return useQuery({
    queryKey: ["meeting-rooms"],
    enabled,
    queryFn: async () => {
      return api.get("/hr/calendar/rooms");
    },
  });
};

export const useCreateMeetingRoom = () => {
  return useMutationWithToast({
    mutationFn: async (data: MeetingRoomCreate) => {
      return api.post("/hr/calendar/rooms", { body: data });
    },
    invalidateQueries: [
      ["meeting-rooms"],
    ],
  });
};

export const useUpdateMeetingRoom = () => {
  return useMutationWithToast({
    mutationFn: async ({ id, ...data }: MeetingRoomUpdate & { id: string }) => {
      return api.patch("/hr/calendar/rooms/{room_id}", { path: { room_id: id }, body: data });
    },
    invalidateQueries: [
      ["meeting-rooms"],
    ],
  });
};

export const useDeleteMeetingRoom = () => {
  return useMutationWithToast({
    mutationFn: async (id: string) => {
      return api.delete("/hr/calendar/rooms/{room_id}", { path: { room_id: id } });
    },
    invalidateQueries: [
      ["meeting-rooms"],
    ],
  });
};

// RSVP for a calendar event. Co-located with calendar events (previously lived in
// hooks/tasks/use-tasks.ts which was the wrong module). The tasks version stays
// for backward compatibility but new callers should use this one.
export const useEventRsvp = () => {
  return useMutationWithToast({
    mutationFn: ({
      eventId,
      status,
    }: {
      eventId: string;
      status: "accepted" | "declined" | "tentative";
    }) =>
      api.patch("/hr/calendar/events/{event_id}/rsvp", { path: { event_id: eventId }, body: { status } }),
    invalidateQueries: [
      ...CALENDAR_INVALIDATION_KEYS,
      // O selo do Calendário na sidebar acompanha cada resposta.
      PENDING_INVITES_KEY,
    ],
    onErrorCallback: (err) => {
      // O servidor já não cria convidados ao responder: 404 = esta pessoa não (ou já não) é convidada.
      if ((err as { status?: number } | null)?.status === 404) toast.error("Já não és convidado deste evento.");
      else toastMutationError(err, "Erro ao responder ao convite");
    },
  });
};

// Add a participant to an existing event. The backend auto-sends an invite
// notification on first add (idempotent — re-add is a no-op + silent).
export const useAddEventAttendee = () => {
  return useMutationWithToast({
    mutationFn: ({ eventId, userId }: { eventId: string; userId: string }) =>
      api.post("/hr/calendar/events/{event_id}/attendees", { path: { event_id: eventId }, body: { user_id: userId } }),
    invalidateQueries: CALENDAR_INVALIDATION_KEYS,
    onErrorCallback: (err) => toastMutationError(err, "Erro ao adicionar convidado"),
  });
};

// Remove a participant. Avisa a pessoa por omissão (é o que o servidor faz). Só se passa
// `notify: false` para uma remoção interna que a pessoa não precisa de ver.
// Backend route is keyed by attendee.id (not user.id).
export const useRemoveEventAttendee = () => {
  return useMutationWithToast({
    mutationFn: ({
      eventId,
      attendeeId,
      notify = true,
    }: {
      eventId: string;
      attendeeId: string;
      notify?: boolean;
    }) =>
      api.delete("/hr/calendar/events/{event_id}/attendees/{attendee_id}", { path: { event_id: eventId, attendee_id: attendeeId }, query: { notify } }),
    invalidateQueries: CALENDAR_INVALIDATION_KEYS,
    onErrorCallback: (err) => toastMutationError(err, "Erro ao remover convidado"),
  });
};

/** O reenvio de convite tem limites: 5 por hora e por evento (429), e não se reenvia a quem já recusou (409). */
export function resendErrorMessage(err: unknown): string {
  const status = (err as { status?: number } | null)?.status;
  if (status === 429) return "Já reenviaste este convite 5 vezes na última hora. Tenta outra vez mais tarde.";
  if (status === 409) return "Esta pessoa já recusou o convite, por isso não é possível reenviá-lo.";
  return calendarErrorMessage(err, "Erro ao reenviar o convite");
}

// Resend the invite notification to an attendee. Useful when the original
// notification was dismissed or the invitee hasn't responded.
export const useResendEventInvite = () => {
  return useMutationWithToast({
    mutationFn: ({ eventId, attendeeId }: { eventId: string; attendeeId: string }) =>
      api.post("/hr/calendar/events/{event_id}/attendees/{attendee_id}/resend", { path: { event_id: eventId, attendee_id: attendeeId } }),
    invalidateQueries: CALENDAR_INVALIDATION_KEYS,
    onErrorCallback: (err) => {
      toast.error(resendErrorMessage(err));
    },
  });
};

// A disponibilidade das salas, o livre/ocupado, os convites por responder e o evento por id
// vivem em `use-calendar-availability.ts`; re-exportam-se daqui para os importadores não mudarem.
export * from "./use-calendar-availability";
